import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Bereich, UserRole } from '@rechnungen/shared';
import { ApiError, api, markFreshLogin, setUnauthorizedHandler } from './api';

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  totpEnabled: boolean;
  /** BL-263: Bereiche, die für diesen Benutzer gesperrt sind. */
  gesperrteBereiche: Bereich[];
}

export type LoginResult = { requires2fa: false } | { requires2fa: true; tempToken: string };

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verify2fa: (tempToken: string, totpCode: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<AuthUser>('/auth/me');
      setUser(res.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        // Netzwerkfehler → nicht den Zustand überschreiben, aber loggen
        console.warn('Auth-Refresh fehlgeschlagen:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Globaler 401-Handler: wenn eine API-Antwort 401 liefert (z.B. Session
  // abgelaufen), User auf null setzen. Die ProtectedRoute leitet dann auf
  // /login um. Login/Me-Checks sind in api.ts schon ausgenommen.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      type LoginResponse = AuthUser | { requires2fa: true; tempToken: string };
      const res = await api.post<LoginResponse>('/auth/login', { email, password });
      if ('requires2fa' in res.data && res.data.requires2fa) {
        // 2FA-Pflicht: Kein Session-Cookie — User bleibt null bis Verify-Schritt
        return { requires2fa: true, tempToken: res.data.tempToken };
      }
      // Kein 2FA: direkt einloggen
      markFreshLogin(3000);
      setUser(res.data as AuthUser);
      await queryClient.invalidateQueries();
      return { requires2fa: false };
    },
    [queryClient],
  );

  const verify2fa = useCallback(
    async (tempToken: string, totpCode: string) => {
      const res = await api.post<AuthUser>('/auth/2fa/verify', { tempToken, totpCode });
      markFreshLogin(3000);
      setUser(res.data);
      await queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);
    setUser(null);
    // Cached Daten der vorherigen Session komplett verwerfen
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo(
    () => ({ user, loading, login, verify2fa, logout, refresh }),
    [user, loading, login, verify2fa, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
  return ctx;
}
